/**
 * Layout principal do painel Admin White Label (/wl)
 * Sidebar + header + ThemeToggle + (opcional) switcher para tenant + Logout
 *
 * CHANGELOG:
 *   v1.1.0 — 2026-05-29 — Adiciona botão "Ir para o tenant" no header quando
 *            o usuário tem cruzamento real (operador WL + membro de qualquer
 *            tenant ativo). Botão grava a escolha em localStorage para evitar
 *            reapresentação da tela /select-context no próximo login. Opção
 *            "Trocar painel" limpa a preferência e devolve a /select-context.
 *            Segurança inalterada — o botão é UX; guards continuam validando.
 */
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { LogOut, Building2 } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useIsWlUser } from '@/hooks/useIsWlUser';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { WhiteLabelSidebar } from '@/components/wl/WhiteLabelSidebar';
import { clearLastContextChoice } from '@/lib/contextSwitcher';

interface Props { children: ReactNode }

export function WhiteLabelLayout({ children }: Props) {
  const { user, signOut, isTenantMember, isAgent } = useAuth();
  const { isWlUser } = useIsWlUser();
  const navigate = useNavigate();

  const handleLogout = () => {
    // signOut faz hard reload para /auth e coordena cross-tab via BroadcastChannel
    signOut();
  };

  // Cruzamento real: só mostra switcher se o usuário pode legitimamente
  // operar como tenant_member em algum tenant ativo.
  const hasCrossover = isWlUser && isTenantMember;

  const goToTenant = () => {
    clearLastContextChoice();
    navigate(isAgent ? '/tenant/chat' : '/tenant', { replace: true });
  };

  return (
    <TooltipProvider>
      <SidebarProvider>
        <div className="app-viewport-min flex w-full">
          <WhiteLabelSidebar />
          <div className="flex-1 min-w-0 relative z-0 flex flex-col">
            <header className="h-14 border-b bg-background flex items-center justify-between px-4">
              <div className="flex items-center gap-2">
                <SidebarTrigger />
                <span className="font-semibold text-foreground">Admin White Label</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Botão direto para o tenant — só aparece se houver cruzamento */}
                {hasCrossover && (
                  <Button variant="outline" size="sm" className="gap-2" onClick={goToTenant}>
                    <Building2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Ir para o tenant</span>
                  </Button>
                )}


                <ThemeToggle />
                <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={handleLogout}>
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p>Sair</p></TooltipContent>
                </Tooltip>
              </div>
            </header>
            <main className="flex-1 min-w-0 overflow-x-hidden p-6 bg-muted/30">
              {children}
            </main>
          </div>
        </div>
      </SidebarProvider>
    </TooltipProvider>
  );
}
